<template>
  <div class="echarts-dashboard">
    <!-- 筛选器区域 -->
    <div class="filter-section">
      <el-select 
        v-model="selectedYear" 
        placeholder="选择年份"
        @change="handleYearChange">
        <el-option
          v-for="year in yearOptions"
          :key="year"
          :label="`${year}年`"
          :value="year">
        </el-option>
      </el-select>

      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        value-format="yyyy-MM-dd"
        @change="fetchData">
      </el-date-picker>
    </div>

    <!-- 图表区域 -->
    <div class="charts-section">
      <!-- 柱状图 -->
      <div class="chart-card">
        <div class="chart-header">
          <h3>{{ selectedYear }}年用户注册趋势</h3>
        </div>
        <div class="chart-content" v-loading="loading" ref="barChart"></div>
      </div>

      <!-- 饼图 -->
      <div class="chart-card">
        <div class="chart-header">
          <h3>用户年龄分布</h3>
        </div>
        <div class="chart-content" v-loading="loading" ref="pieChart"></div>
      </div>
    </div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  data() {
    return {
      loading: false,
      selectedYear: new Date().getFullYear(),
      yearOptions: [2023, 2024, 2025],
      dateRange: [],
      barChart: null,
      pieChart: null,
      
      // 测试数据
      testUsers: [
        { createTime: '2025-03-15 10:00:00', age: 20 },
        { createTime: '2025-03-20 11:00:00', age: 30 },
        { createTime: '2025-03-20 11:00:00', age: 30 },
        { createTime: '2025-04-05 09:00:00', age: 17 },
        { createTime: '2025-05-12 14:00:00', age: 40 },
        { createTime: '2025-05-18 16:00:00', age: 22 },
        { createTime: '2025-06-01 08:00:00', age: 28 },
        { createTime: '2025-06-15 13:00:00', age: 35 },
        { createTime: '2025-07-22 17:00:00', age: 42 },
        { createTime: '2025-08-09 10:00:00', age: 19 },
        { createTime: '2025-09-30 16:00:00', age: 27 },
        { createTime: '2025-10-12 11:00:00', age: 50 },
        { createTime: '2025-11-25 14:00:00', age: 33 },
        { createTime: '2025-12-05 09:00:00', age: 28 }
      ]
    }
  },

  mounted() {
    this.initCharts()
    this.fetchData()
  },

  methods: {
    //日期格式化
    parseTime(date, pattern = '{y}-{m}-{d}') {
      return pattern
      .replace('{y}', date.getFullYear())
      .replace('{m}', String(date.getMonth() + 1).padStart(2, '0'))
      .replace('{d}', String(date.getDate()).padStart(2, '0'))
    },
    initCharts() {
      this.barChart = echarts.init(this.$refs.barChart)
      this.pieChart = echarts.init(this.$refs.pieChart)
      window.addEventListener('resize', this.resizeHandler)
    },

    async fetchData() {
      this.loading = true
      try {
        // 实际项目中使用API请求
        // const params = {
        //   year: this.selectedYear,
        //   startDate: this.dateRange[0],
        //   endDate: this.dateRange[1]
        // }
        // const res = await api.getUserData(params)
        
        // 使用测试数据
        const processedData = this.processData(this.testUsers)
        this.renderBarChart(processedData.monthlyData)
        this.renderPieChart(processedData.ageData)
      } catch (error) {
        console.error(error)
        this.$message.error('数据加载失败')
      } finally {
        this.loading = false
      }
    },

    processData(users) {
      // 日期筛选
      const filtered = users.filter(user => {
        const createDate = new Date(user.createTime)
        const year = createDate.getFullYear()
        const date = this.parseTime(createDate, '{y}-{m}-{d}')

        // 年份匹配
        if (year !== this.selectedYear) return false
        
        // 日期范围筛选
        if (this.dateRange && this.dateRange.length === 2) {
          return date >= this.dateRange[0] && date <= this.dateRange[1]
        }
        return true
      })

      // 按月统计
      const monthlyData = Array.from({ length: 12 }, (_, i) => ({
        month: `${i + 1}月`,
        count: 0
      }))

      filtered.forEach(user => {
        const month = new Date(user.createTime).getMonth()
        monthlyData[month].count++
      })

      // 年龄分布统计
      const ageGroups = {
        '18岁以下': 0,
        '18-25岁': 0,
        '26-35岁': 0,
        '36-45岁': 0,
        '46岁以上': 0
      }

      filtered.forEach(user => {
        const age = user.age
        if (age < 18) {
          ageGroups['18岁以下']++
        } else if (age <= 25) {
          ageGroups['18-25岁']++
        } else if (age <= 35) {
          ageGroups['26-35岁']++
        } else if (age <= 45) {
          ageGroups['36-45岁']++
        } else {
          ageGroups['46岁以上']++
        }
      })

      return {
        monthlyData,
        ageData: Object.entries(ageGroups).map(([name, value]) => ({ name, value }))
      }
    },

    renderBarChart(data) {
      const option = {
        tooltip: {
          trigger: 'axis'
        },
        xAxis: {
          type: 'category',
          data: data.map(item => item.month)
        },
        yAxis: {
          type: 'value'
        },
        series: [{
          data: data.map(item => item.count),
          type: 'bar',
          itemStyle: {
            color: '#409EFF'
          },
          label: {
            show: true,
            position: 'top'
          }
        }]
      }
      this.barChart.setOption(option)
    },

    renderPieChart(data) {
      const option = {
        color:['#409EFF','#67C23A','#E6A23C','#F56C6C','#909399'],
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          data: data.map(item => item.name)
        },
        series: [{
          name: '年龄分布',
          type: 'pie',
          radius: '50%',
          data: data,
          emphasis: {
            itemStyle: {
              
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }]
      }
      this.pieChart.setOption(option)
    },

    handleYearChange(year) {
      // 自动设置日期范围为该年年初至年末
      this.dateRange = [`${year}-01-01`, `${year}-12-31`]
      this.fetchData()
    },

    resizeHandler() {
      this.barChart.resize()
      this.pieChart.resize()
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler)
  }
}
</script>

<style scoped>
.echarts-dashboard {
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f7fa;
}

/* 筛选器区域样式 */
.filter-section {
  background: #fff;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}

/* 图表区域样式 */
.charts-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(600px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

/* 图表卡片样式 */
.chart-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.chart-header {
  padding: 16px 24px;
  border-bottom: 1px solid #ebeef5;
}

.chart-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.chart-content {
  height: 400px;
  padding: 24px;
}

/* Element UI 组件样式覆盖 */
.el-select {
  width: 120px;
}

.el-date-editor.el-input,
.el-date-editor.el-input__inner {
  width: 320px;
}

/* 响应式布局 */
@media screen and (max-width: 1200px) {
  .charts-section {
    grid-template-columns: 1fr;
  }
  
  .chart-content {
    height: 350px;
  }
}

@media screen and (max-width: 768px) {
  .echarts-dashboard {
    padding: 12px;
  }

  .filter-section {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
  }

  .filter-section > * {
    width: 100% !important;
  }

  .chart-card {
    margin-bottom: 16px;
  }

  .chart-header {
    padding: 12px 16px;
  }

  .chart-content {
    padding: 16px;
    height: 300px;
  }
}

/* 自定义滚动条 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f5f7fa;
}

::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #c0c4cc;
}
</style>